import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const FORM_TITLE = 'ENGINEERING DEFECT REPORTING FORM';

const CYCLE_LABELS = [
  { key: 'ng', label: 'Ng' },
  { key: 'nf', label: 'Nf' },
  { key: 'np', label: 'Np' },
  { key: 'ct', label: 'CT' },
  { key: 'pt', label: 'PT' },
  { key: 'imp', label: 'IMP' },
  { key: 'n1', label: 'N1' },
  { key: 'n2', label: 'N2' },
  { key: 'creep', label: 'Creep' }
];

const emptyCycles = () =>
  CYCLE_LABELS.reduce((acc, { key }) => {
    acc[key] = '';
    return acc;
  }, {});

function getAircraftIdFromWorkOrder(workOrder) {
  const ac = workOrder?.aircraftId;
  if (!ac) return null;
  if (typeof ac === 'object' && ac._id) return String(ac._id);
  return String(ac);
}

function sortTsnByDateDesc(tsnArray) {
  if (!Array.isArray(tsnArray) || !tsnArray.length) return [];
  return [...tsnArray].sort((a, b) => {
    const ta = new Date(a.asOnDate || a.manufacturingDate || 0).getTime();
    const tb = new Date(b.asOnDate || b.manufacturingDate || 0).getTime();
    return tb - ta;
  });
}

/** Airframe hours: flightHours field, else latest TSN Hours / additionalFields.Hours */
function getAircraftHoursFromAc(ac) {
  if (!ac) return '';
  const fh = ac.flightHours;
  if (fh != null && fh !== '' && Number.isFinite(Number(fh)) && Number(fh) > 0) {
    return String(fh);
  }
  for (const entry of sortTsnByDateDesc(ac.tsn)) {
    const p = String(entry?.period || '').trim();
    const pv = String(entry?.periodValue || '').trim();
    if ((p === 'Hours' || p.toLowerCase() === 'hours') && pv) return pv;
    const af = entry?.additionalFields;
    if (af && typeof af === 'object') {
      const h = af.Hours ?? af.hours ?? af.HOURS;
      if (h != null && String(h).trim() !== '') return String(h).trim();
    }
  }
  return '';
}

function getAircraftLandingsFromAc(ac) {
  if (!ac) return '';
  for (const entry of sortTsnByDateDesc(ac.tsn)) {
    if (entry?.landings != null && String(entry.landings).trim() !== '') return String(entry.landings);
  }
  return '';
}

/** Category (e.g. Helicopter) + manufacturer + model */
function formatAircraftTypeLine(ac) {
  if (!ac || typeof ac !== 'object') return '';
  const bits = [];
  if (ac.aircraftType) bits.push(String(ac.aircraftType).trim());
  const mm = [ac.manufacturer, ac.model].filter((x) => x && String(x).trim()).join(' ').trim();
  if (mm) bits.push(mm);
  return bits.join(' — ').trim();
}

function pickEngineAssemblies(assemblies) {
  const list = Array.isArray(assemblies) ? assemblies : [];
  const engines = list.filter((a) => {
    const tn = String(a?.typeName || '').toLowerCase();
    return tn.includes('engine');
  });
  engines.sort((a, b) => {
    const pa = parseInt(String(a.position || '').replace(/\D/g, '') || '0', 10);
    const pb = parseInt(String(b.position || '').replace(/\D/g, '') || '0', 10);
    if (pa !== pb) return pa - pb;
    return String(a.serialNumber || '').localeCompare(String(b.serialNumber || ''));
  });
  return engines;
}

function engineHoursFromAssembly(asm) {
  const recs = asm?.timeSinceRecords;
  if (!Array.isArray(recs)) return '';
  const tsnHours = recs.find(
    (r) =>
      r.type === 'TSN' &&
      r.elapsedTime?.value &&
      String(r.elapsedTime.unit || '').toLowerCase().includes('hour')
  );
  if (tsnHours?.elapsedTime) {
    return `${tsnHours.elapsedTime.value} ${tsnHours.elapsedTime.unit || ''}`.trim();
  }
  const any = recs.find((r) => r.elapsedTime?.value && r.elapsedTime?.unit);
  return any ? `${any.elapsedTime.value} ${any.elapsedTime.unit}`.trim() : '';
}

/** Map assembly sinceNewValues / TSR labels into Ng, Nf, … when names match */
function cyclesFromAssembly(asm) {
  const c = emptyCycles();
  const snv = asm?.sinceNewValues;
  if (!Array.isArray(snv)) return c;
  for (const row of snv) {
    const p = String(row?.period || '').trim();
    const val = String(row?.assemblyValue ?? '').trim();
    if (!p || !val) continue;
    const pl = p.toLowerCase();
    const hit = CYCLE_LABELS.find(
      ({ key, label }) => pl === key.toLowerCase() || pl === label.toLowerCase()
    );
    if (hit) c[hit.key] = val;
  }
  return c;
}

/** Display string for a total-carried-forward scalar (numbers keep sensible string form). */
function formatTcfScalar(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'number' && Number.isFinite(val)) {
    if (Math.abs(val - Math.round(val)) < 1e-9) return String(Math.round(val));
    return String(val);
  }
  return String(val).trim();
}

function mergeTotalsCustomFieldsIntoCarriedForward(tcf, entry) {
  const mix = entry?.totalsCustomFields;
  if (!mix || typeof mix !== 'object') return tcf && typeof tcf === 'object' ? { ...tcf } : {};
  const out = tcf && typeof tcf === 'object' ? { ...tcf } : {};
  for (const [key, val] of Object.entries(mix)) {
    if (typeof key !== 'string' || !key.startsWith('totalCarriedForward.')) continue;
    const baseKey = key.slice(key.lastIndexOf('.') + 1);
    const cur = out[baseKey];
    const hasPrimary = !(cur == null || cur === '');
    if (hasPrimary) continue;
    if (val == null || val === '') continue;
    out[baseKey] = val;
  }
  return out;
}

/** Same selection as MroBoard.ensureLatestApprovedTlbLoaded — latest APPROVED by approval/update time. */
function pickLatestApprovedTlb(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const approved = list
    .filter((e) => e?.workflowStatus === 'APPROVED')
    .sort((a, b) => {
      const at = new Date(a.approvedAt || a.updatedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.approvedAt || b.updatedAt || b.createdAt || 0).getTime();
      return bt - at;
    });
  const latest = approved[0];
  if (!latest) return null;
  const raw =
    latest.totalCarriedForward && typeof latest.totalCarriedForward === 'object' ? latest.totalCarriedForward : {};
  const totalCarriedForward = mergeTotalsCustomFieldsIntoCarriedForward(raw, latest);
  return { entry: latest, totalCarriedForward };
}

function mergeTlbCarriedForwardIntoForm(base, tcf) {
  if (!tcf || typeof tcf !== 'object') return { ...base };
  const next = { ...base };
  const v = formatTcfScalar;

  if (tcf.airframeHours != null && String(tcf.airframeHours).trim() !== '') {
    next.aircraftHours = v(tcf.airframeHours);
  }
  const ldg = tcf.landings ?? tcf.totalLandings;
  if (ldg != null && String(ldg).trim() !== '') {
    next.aircraftLandings = v(ldg);
  }
  if (tcf.engine1Hours != null && String(tcf.engine1Hours).trim() !== '') {
    next.engine1Hours = v(tcf.engine1Hours);
  }
  if (tcf.engine2Hours != null && String(tcf.engine2Hours).trim() !== '') {
    next.engine2Hours = v(tcf.engine2Hours);
  }

  const c1 = { ...next.engine1Cycles };
  const c2 = { ...next.engine2Cycles };
  const setC = (target, key, raw) => {
    const s = v(raw);
    if (s) target[key] = s;
  };
  setC(c1, 'ct', tcf.lhEngineCyclesCT);
  setC(c1, 'pt', tcf.lhEngineCyclesPT);
  setC(c1, 'imp', tcf.lhEngineCyclesIMP);
  setC(c2, 'ct', tcf.rhEngineCyclesCT);
  setC(c2, 'pt', tcf.rhEngineCyclesPT);
  setC(c2, 'imp', tcf.rhEngineCyclesIMP);

  next.engine1Cycles = c1;
  next.engine2Cycles = c2;
  return next;
}

function mergeCycleGap(targetCycles, assemblyCycles, key) {
  const cur = String(targetCycles[key] || '').trim();
  if (cur) return targetCycles[key];
  const fromAsm = String(assemblyCycles[key] || '').trim();
  return fromAsm || '';
}

/** Serials (and gaps) from assemblies; does not overwrite hours/cycles already set from the tech log. */
function mergeEngineAssemblyGapFill(base, assemblies) {
  const engines = pickEngineAssemblies(assemblies);
  const next = { ...base };
  if (engines[0]) {
    if (engines[0].serialNumber) next.engine1Serial = engines[0].serialNumber;
    const h1 = engineHoursFromAssembly(engines[0]);
    if (h1 && !String(next.engine1Hours || '').trim()) next.engine1Hours = h1;
    const asmC1 = cyclesFromAssembly(engines[0]);
    const c1 = { ...next.engine1Cycles };
    for (const { key } of CYCLE_LABELS) {
      c1[key] = mergeCycleGap(c1, asmC1, key);
    }
    next.engine1Cycles = c1;
  }
  if (engines[1]) {
    if (engines[1].serialNumber) next.engine2Serial = engines[1].serialNumber;
    const h2 = engineHoursFromAssembly(engines[1]);
    if (h2 && !String(next.engine2Hours || '').trim()) next.engine2Hours = h2;
    const asmC2 = cyclesFromAssembly(engines[1]);
    const c2 = { ...next.engine2Cycles };
    for (const { key } of CYCLE_LABELS) {
      c2[key] = mergeCycleGap(c2, asmC2, key);
    }
    next.engine2Cycles = c2;
  }
  return next;
}

function formatApprovedDateLabel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function buildInitialForm(workOrder, user) {
  const ac = workOrder?.aircraftId && typeof workOrder.aircraftId === 'object' ? workOrder.aircraftId : {};
  const aircraftType = formatAircraftTypeLine(ac);
  const aircraftRegistration = (ac.registration && String(ac.registration).trim()) || '';
  const aircraftHours = getAircraftHoursFromAc(ac);
  const aircraftLandings = getAircraftLandingsFromAc(ac);
  const today = new Date().toISOString().slice(0, 10);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  return {
    formNumber: '',
    edrReferenceNumber: '',
    referenceDate: today,
    aircraftType,
    aircraftRegistration,
    aircraftHours,
    aircraftLandings,
    engine1Serial: '',
    engine2Serial: '',
    engine1Hours: '',
    engine2Hours: '',
    engine1Cycles: emptyCycles(),
    engine2Cycles: emptyCycles(),
    defectDateStation: '',
    icplNumber: '',
    workOrderRefNo: workOrder?.workOrderNumber || workOrder?.title || '',
    snagReportedBy: displayName ? `${displayName}${user?.authorityNumber ? `, #${user.authorityNumber}` : ''}` : '',
    defectDetails: workOrder?.description || workOrder?.title || '',
    affectedPartsDetails: '',
    partNumber: '',
    serialNumber: '',
    ameManHours: '',
    techManHours: '',
    proposedRectification: '',
    edrSentNote: '',
    raisingPersonName: displayName,
    designation: '',
    signatureNote: '',
    raisingDate: today,
    raisingPlace: ''
  };
}

const CycleFields = ({ label, value, onChange }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {label}
    </Typography>
    <Grid container spacing={1}>
      {CYCLE_LABELS.map(({ key, label: ln }) => (
        <Grid item xs={6} sm={4} key={key}>
          <TextField
            fullWidth
            size="small"
            label={ln}
            value={value[key] || ''}
            onChange={(e) => onChange(key, e.target.value)}
          />
        </Grid>
      ))}
    </Grid>
  </Box>
);

/**
 * Engineering Defect Reporting (EDR) form — digital capture for MRO workbench.
 */
const EngineeringDefectReportDialog = ({ open, onClose, workOrder, user }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(() => buildInitialForm(null, user));
  const [aircraftAutofillLoading, setAircraftAutofillLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingToCamo, setSendingToCamo] = useState(false);
  const [tlbAutofillMeta, setTlbAutofillMeta] = useState(null);
  const [edrReferenceAssigned, setEdrReferenceAssigned] = useState(false);

  const woId = workOrder?._id;

  const tlbAutofillNote = useMemo(() => {
    if (!tlbAutofillMeta?.applied) return '';
    const parts = [];
    if (tlbAutofillMeta.sheetNumber) parts.push(`sheet ${tlbAutofillMeta.sheetNumber}`);
    if (tlbAutofillMeta.approvedDate) parts.push(`approved ${tlbAutofillMeta.approvedDate}`);
    const suffix = parts.length ? ` (${parts.join(', ')})` : '';
    return `Aircraft hours, landings, and engine hours/cycles are prefilled from the latest approved Technical Logbook${suffix}.`;
  }, [tlbAutofillMeta]);

  useEffect(() => {
    if (!open) {
      setTlbAutofillMeta(null);
      setEdrReferenceAssigned(false);
    }
  }, [open]);

  const loadAutofilledForm = useCallback(async () => {
    if (!workOrder?._id) return;
    setAircraftAutofillLoading(true);
    setTlbAutofillMeta(null);
    setEdrReferenceAssigned(false);
    try {
      let next = buildInitialForm(workOrder, user);
      const aircraftId = getAircraftIdFromWorkOrder(workOrder);
      const [tlbRes, asmRes, edrRes] = await Promise.all([
        aircraftId
          ? axios.get(`/api/technical-log-book/aircraft/${aircraftId}`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        aircraftId
          ? axios
              .get('/api/assemblies', {
                params: { aircraftId, pageSize: 500, page: 1, isActive: 'true' }
              })
              .catch(() => ({ data: { assemblies: [] } }))
          : Promise.resolve({ data: { assemblies: [] } }),
        axios.get(`/api/maintenance/${workOrder._id}/edr`).catch(() => ({ data: {} }))
      ]);
      const picked = pickLatestApprovedTlb(tlbRes.data);
      if (picked?.entry) {
        setTlbAutofillMeta({
          applied: true,
          sheetNumber: (picked.entry.sheetNumber && String(picked.entry.sheetNumber).trim()) || '',
          approvedDate: formatApprovedDateLabel(
            picked.entry.approvedAt || picked.entry.updatedAt || picked.entry.createdAt
          )
        });
        next = mergeTlbCarriedForwardIntoForm(next, picked.totalCarriedForward);
      }
      const raw = asmRes.data?.assemblies ?? asmRes.data;
      const list = Array.isArray(raw) ? raw : [];
      next = mergeEngineAssemblyGapFill(next, list);

      const edrData = edrRes.data || {};
      if (edrData.draft?.form && typeof edrData.draft.form === 'object') {
        next = { ...next, ...edrData.draft.form };
      }
      if (edrData.edrReferenceNumber) {
        next = { ...next, edrReferenceNumber: edrData.edrReferenceNumber };
      }
      setEdrReferenceAssigned(Boolean(edrData.assigned));
      setForm(next);
    } finally {
      setAircraftAutofillLoading(false);
    }
  }, [workOrder, user]);

  useEffect(() => {
    if (open && woId) {
      loadAutofilledForm();
    }
  }, [open, woId, loadAutofilledForm]);

  const set = (path, v) => {
    if (path.startsWith('engine1Cycles.') || path.startsWith('engine2Cycles.')) {
      const [prefix, key] = path.split('.');
      setForm((prev) => ({
        ...prev,
        [prefix]: { ...prev[prefix], [key]: v }
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [path]: v }));
  };

  const title = useMemo(
    () => (workOrder ? `EDR — ${workOrder.workOrderNumber || workOrder.title || workOrder._id}` : 'Engineering Defect Reporting Form'),
    [workOrder]
  );

  const edrBusy = savingDraft || sendingToCamo;

  const handleSaveDraft = async () => {
    if (!workOrder?._id) return;
    setSavingDraft(true);
    try {
      const res = await axios.post(`/api/maintenance/${workOrder._id}/edr/save`, {
        form,
        icplNumber: form.icplNumber
      });
      const assignedRef = res.data?.edrReferenceNumber;
      if (assignedRef) {
        setForm((prev) => ({ ...prev, edrReferenceNumber: assignedRef }));
        setEdrReferenceAssigned(true);
      }
      showToast('EDR draft saved', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save EDR draft', 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSendToCamo = async () => {
    if (!workOrder?._id) return;
    setSendingToCamo(true);
    try {
      await axios.post(`/api/maintenance/${workOrder._id}/edr/send-to-camo`, {
        form,
        icplNumber: form.icplNumber
      });
      showToast('EDR sent to CAMO', 'success');
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send EDR to CAMO', 'error');
    } finally {
      setSendingToCamo(false);
    }
  };

  if (!workOrder) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ '@media print': { boxShadow: 'none' } }} className="edr-dialog-print">
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            label="FORM No."
            placeholder="e.g. ICPL/QAD/044 ISSUE-01, REV 31"
            value={form.formNumber}
            onChange={(e) => set('formNumber', e.target.value)}
          />
          <Box sx={{ textAlign: 'center', border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1">{FORM_TITLE}</Typography>
          </Box>

          <Typography variant="subtitle2" color="primary">
            EDR reference
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="EDR reference number"
                value={form.edrReferenceNumber}
                InputProps={{ readOnly: true }}
                helperText={
                  edrReferenceAssigned
                    ? 'System generated'
                    : 'Preview — assigned when you save or send'
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Reference date"
                InputLabelProps={{ shrink: true }}
                value={form.referenceDate}
                onChange={(e) => set('referenceDate', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle2" color="primary">
              Aircraft & engine
            </Typography>
            {aircraftAutofillLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading aircraft usage…
              </Typography>
            )}
          </Stack>
          {tlbAutofillNote ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {tlbAutofillNote}
            </Typography>
          ) : null}
          {aircraftAutofillLoading && <LinearProgress sx={{ mb: 1 }} />}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Aircraft (type / model)"
                placeholder="e.g. A109S"
                value={form.aircraftType}
                onChange={(e) => set('aircraftType', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Registration"
                placeholder="e.g. VT-BGM"
                value={form.aircraftRegistration}
                onChange={(e) => set('aircraftRegistration', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Aircraft hours"
                value={form.aircraftHours}
                onChange={(e) => set('aircraftHours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Aircraft landings"
                value={form.aircraftLandings}
                onChange={(e) => set('aircraftLandings', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine serial no. (No.1)"
                value={form.engine1Serial}
                onChange={(e) => set('engine1Serial', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine serial no. (No.2)"
                value={form.engine2Serial}
                onChange={(e) => set('engine2Serial', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine hours (No.1)"
                value={form.engine1Hours}
                onChange={(e) => set('engine1Hours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine hours (No.2)"
                value={form.engine2Hours}
                onChange={(e) => set('engine2Hours', e.target.value)}
              />
            </Grid>
          </Grid>

          <CycleFields
            label="Engine cycles — No.1"
            value={form.engine1Cycles}
            onChange={(key, v) => set(`engine1Cycles.${key}`, v)}
          />
          <CycleFields
            label="Engine cycles — No.2"
            value={form.engine2Cycles}
            onChange={(key, v) => set(`engine2Cycles.${key}`, v)}
          />

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Defect context
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Date & station of defect recorded"
                placeholder="e.g. 18/04/2024, JAKKUR"
                value={form.defectDateStation}
                onChange={(e) => set('defectDateStation', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Authorisation number"
                value={form.icplNumber}
                onChange={(e) => set('icplNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Work order ref. no."
                value={form.workOrderRefNo}
                onChange={(e) => set('workOrderRefNo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Snag reported by (name / authority no.)"
                value={form.snagReportedBy}
                onChange={(e) => set('snagReportedBy', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Defect description &amp; parts
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Details of defect"
            value={form.defectDetails}
            onChange={(e) => set('defectDetails', e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Details of affected parts"
            value={form.affectedPartsDetails}
            onChange={(e) => set('affectedPartsDetails', e.target.value)}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Part no." value={form.partNumber} onChange={(e) => set('partNumber', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Serial no." value={form.serialNumber} onChange={(e) => set('serialNumber', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="AME man hours (identification)"
                value={form.ameManHours}
                onChange={(e) => set('ameManHours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Tech. man hours (identification)"
                value={form.techManHours}
                onChange={(e) => set('techManHours', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Rectification
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Proposed rectification action"
            value={form.proposedRectification}
            onChange={(e) => set('proposedRectification', e.target.value)}
          />

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Submission & authorization
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="EDR sent / distribution note"
            placeholder="e.g. Sent date — to CAMO / operator &/or CAR-145 organization"
            value={form.edrSentNote}
            onChange={(e) => set('edrSentNote', e.target.value)}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Name of person raising EDR"
                value={form.raisingPersonName}
                onChange={(e) => set('raisingPersonName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Designation" placeholder="e.g. AME" value={form.designation} onChange={(e) => set('designation', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Signature (name / file ref. / digital id)"
                value={form.signatureNote}
                onChange={(e) => set('signatureNote', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={form.raisingDate}
                onChange={(e) => set('raisingDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Place" value={form.raisingPlace} onChange={(e) => set('raisingPlace', e.target.value)} />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ '@media print': { display: 'none' } }}>
        <Button onClick={onClose} disabled={edrBusy}>
          Close
        </Button>
        <Button variant="outlined" onClick={handleSaveDraft} disabled={edrBusy || aircraftAutofillLoading}>
          {savingDraft ? 'Saving…' : 'Save draft'}
        </Button>
        <Button variant="contained" onClick={handleSendToCamo} disabled={edrBusy || aircraftAutofillLoading}>
          {sendingToCamo ? 'Sending…' : 'Send to CAMO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EngineeringDefectReportDialog;
